import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import Rating from 'modules/core/components/Rating'
import TextArea from 'modules/core/components/TextArea'
import i18n from 'modules/core/lib/i18n'

import { Element } from 'modules/forms/lib/Element'
import { Answer } from 'modules/forms/lib/types'

interface RatingElementQuestionProps {
  element: Element
  hasAttemptedSubmit?: boolean
  onChange?: (answer: Answer) => void
  disabled?: boolean
}

/**
 * Editor component for RatingElement
 */
export const RatingElementQuestion: React.FC<RatingElementQuestionProps> = ({
  element,
  hasAttemptedSubmit = false,
  onChange,
  disabled,
}) => {
  const currentChoices = element.answer && element.answer.options ? element.answer.options : []
  const [choiceAnswers, setChoiceAnswers] = React.useState<string[]>(currentChoices)

  const currentCommentValue = element.answer ? element.answer.comment : undefined
  const [commentValue, setCommentValue] = React.useState<string | undefined>(currentCommentValue)

  const ratingValue =
    choiceAnswers.length > 0
      ? Number(element.options.find((option) => option.id?.toString() === choiceAnswers[0])?.value)
      : undefined

  const handleRatingChange = (value: number | undefined) => {
    const selectedOption = element.options.find((option) => Number(option.value) === value)
    const selectedOptions = selectedOption?.id ? [selectedOption.id.toString()] : []
    setChoiceAnswers(selectedOptions)

    onChange?.({
      answer: String(value ?? ''),
      options: selectedOptions,
      comment: commentValue,
    })
  }

  const handleCommentChange = (value: string | undefined) => {
    setCommentValue(value)
    onChange?.({
      answer: String(ratingValue ?? ''),
      comment: value,
      options: choiceAnswers,
    })
  }

  const getError = () => {
    if (hasAttemptedSubmit) {
      if (element.mandatory && choiceAnswers.length === 0) {
        return i18n.t('forms.render.answer_required')
      }
    }
    return undefined
  }

  const [hoveredRating, setHoveredRating] = React.useState<number | null>(null)
  const ratingLabels = element.options.reduce<Record<number, string>>((acc, item) => {
    const label = { [item.value]: `${item.value}. ${item.label}` }
    return { ...acc, ...label }
  }, {})

  const range = element.options.map((r) => Number(r.value))
  const min = Math.min(...range)
  const max = Math.max(...range)

  const optionalComment = element.params.optionalComment ?? undefined

  return (
    <Box gap="s16">
      <Rating
        hasTooltip
        value={ratingValue}
        max={max}
        min={min}
        error={getError()}
        onClick={handleRatingChange}
        onHover={setHoveredRating}
        // This is a hack, the rest of the inputs hide the mandatory icon when there is a value
        // but not the rating input. Since this component is deprecated we can't touch it,
        // so we mark it as optional it there is a value. This does not affect the behaviour, since this prop
        // only shows the mandatory icon, there is no more logic to it.
        mandatory={element.mandatory && ratingValue === undefined}
        readonly={disabled}
        tooltipLabel={hoveredRating !== null ? ratingLabels[hoveredRating] : ''}
      />
      {optionalComment && (
        <TextArea
          hideLabel
          placeholder={i18n.t('forms.render.answer_placeholder')}
          value={commentValue}
          onChange={handleCommentChange}
          readonly={disabled}
          error={getError()}
        />
      )}
    </Box>
  )
}
